import { Dispatch, SetStateAction, useMemo, useState } from 'react'

import { CampaignReview } from 'src/Hooks/useCampaignDetailsReviews'

enum ReviewTabKey {
  ALL = 'all',
  REVIEWED = 'reviewed',
  NOT_REVIEWED = 'notReviewed',
}

const REVIEWED_STATUSES = new Set(['placement', 'feedback'])

const STATUS_SORT_ORDER: Record<string, number> = {
  placement: 0,
  feedback: 0,
  awaiting: 1,
  notReviewed: 2,
}

const UNKNOWN_STATUS_ORDER = 3

const sortReviewsForAllTab = (reviews: CampaignReview[]): CampaignReview[] =>
  [...reviews].sort((a, b) => {
    const orderA = STATUS_SORT_ORDER[a.status ?? ''] ?? UNKNOWN_STATUS_ORDER
    const orderB = STATUS_SORT_ORDER[b.status ?? ''] ?? UNKNOWN_STATUS_ORDER

    if (orderA !== orderB) {
      return orderA - orderB
    }

    if (orderA === 0 && a.reviewDate && b.reviewDate) {
      return new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime()
    }

    return 0
  })

interface UseReviewTabsReturn {
  activeTab: string
  setActiveTab: Dispatch<SetStateAction<string>>
  filteredReviews: CampaignReview[]
  tabItems: Array<{ key: string; label: string; count: number }>
  isReviewedEmpty: boolean
  showNotReviewedBanner: boolean
}

export const useReviewTabs = (
  reviews?: CampaignReview[],
): UseReviewTabsReturn => {
  const [activeTab, setActiveTab] = useState<string>(ReviewTabKey.ALL)

  const reviewedReviews = useMemo(
    () => reviews?.filter((r) => REVIEWED_STATUSES.has(r.status ?? '')) ?? [],
    [reviews],
  )

  const notReviewedReviews = useMemo(
    () => reviews?.filter((r) => r.status === 'notReviewed') ?? [],
    [reviews],
  )

  const filteredReviews = useMemo(() => {
    if (activeTab === ReviewTabKey.REVIEWED) {
      return reviewedReviews
    }
    if (activeTab === ReviewTabKey.NOT_REVIEWED) {
      return notReviewedReviews
    }
    return sortReviewsForAllTab(reviews ?? [])
  }, [activeTab, reviews, reviewedReviews, notReviewedReviews])

  const tabItems = useMemo(
    () => [
      {
        key: ReviewTabKey.ALL,
        label: 'All',
        count: reviews?.length ?? 0,
      },
      {
        key: ReviewTabKey.REVIEWED,
        label: 'Reviewed',
        count: reviewedReviews.length,
      },
      {
        key: ReviewTabKey.NOT_REVIEWED,
        label: 'Not reviewed',
        count: notReviewedReviews.length,
      },
    ],
    [reviews?.length, reviewedReviews.length, notReviewedReviews.length],
  )

  const isReviewedEmpty =
    activeTab === ReviewTabKey.REVIEWED && filteredReviews.length === 0

  const showNotReviewedBanner =
    activeTab === ReviewTabKey.NOT_REVIEWED && notReviewedReviews.length > 0

  return {
    activeTab,
    setActiveTab,
    filteredReviews,
    tabItems,
    isReviewedEmpty,
    showNotReviewedBanner,
  }
}
